import { useCallback, useRef, useState } from 'react';
import { Link } from 'wouter';
import { ASSESSMENT_NAV, PRIMARY_NAV } from '../../lib/shell/navigation';
import { useDisclosure } from '../../lib/shell/useDisclosure';
import { ChevronDown } from '../marketing/icons';
import { Button } from '../ui/button';

/**
 * Desktop navigation for the Experience Shell (specification 3.1).
 *
 * The assessments group is a disclosure. Legacy implements the same shape but
 * only for the mouse: no keyboard dismissal, and no assistive expanded state.
 * Both are supplied here through `useDisclosure`, which also restores focus to
 * the trigger on Escape.
 */
export function DesktopNav() {
  const [open, setOpen] = useState(false);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const close = useCallback(() => setOpen(false), []);

  useDisclosure(open, close, panelRef, triggerRef);

  return (
    <nav aria-label="Primary" data-testid="desktop-nav" className="hidden items-center gap-6 md:flex">
      <div className="relative">
        <Button
          ref={triggerRef}
          type="button"
          variant="ghost"
          size="sm"
          aria-expanded={open}
          aria-controls="assessments-menu"
          data-testid="assessments-trigger"
          onClick={() => setOpen((previous) => !previous)}
        >
          Assessments
          {/* Legacy marks the group as a disclosure with a chevron that turns
              over while the panel is open. Without it the trigger reads as a
              plain link and gives no sign there is anything behind it. */}
          <ChevronDown
            className={`w-3.5 h-3.5 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </Button>
        {open ? (
          <div
            ref={panelRef}
            id="assessments-menu"
            data-testid="assessments-menu"
            className="absolute left-0 z-40 mt-2 w-64 rounded-xl border border-border bg-popover p-2 text-popover-foreground shadow"
          >
            {ASSESSMENT_NAV.map((item) => (
              <Link
                key={item.href}
                href={item.href}
                onClick={close}
                className="block rounded-md px-3 py-2 text-sm transition-colors hover:bg-secondary hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              >
                {item.label}
              </Link>
            ))}
          </div>
        ) : null}
      </div>

      {PRIMARY_NAV.map((item) => (
        <Link
          key={item.href}
          href={item.href}
          className="text-sm font-medium transition-colors hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
        >
          {item.label}
        </Link>
      ))}
    </nav>
  );
}
